import type { MouseEvent } from 'react';
import styled from 'styled-components';
import { Typography } from '@elfsight/widget-ui';
import { useSettings } from '@wu/widget-core';
import type { FileEmbedsFile, ItemFileType } from '../../types';
import { Icon } from '../common';
import {
  getClassName as defaultGetClassName,
  getLinkAriaLabel
} from '../helpers';

const getClassName = (name: string) => defaultGetClassName('item', name);

interface ItemProps {
  item: FileEmbedsFile;
}

const extensions: Record<ItemFileType, RegExp> = {
  xls: /xlsx|xlsb|xls|xlsm/,
  doc: /docx|doc|txt|rtf/,
  pp: /pptx|ppsx|ppt|pps|potx|ppsm/,
  pdf: /pdf/,
  img: /jpeg|jpg|png|gif|svg/,
  audio: /wav|mp3/
};

export const Item = ({ item }: ItemProps) => {
  const {
    layout,
    fileBorderSize,
    fileBorderColor,
    showDownloadLink,
    downloadLinkText,
    nameTextStyle,
    nameColor,
    showIcon,
    clickAction,
    downloadTextStyle,
    downloadColor,
    iconColor,
    fileBackgroundColor,
    previewImageSize,
    icon
  } = useSettings();

  const { name, link, previewImage, altText } = item;

  const { url, extension } = link ?? {};

  const fileType = Object.keys(extensions).find(
    (key) => extension && extensions[key].test(extension)
  );

  const clickHandler = (e: MouseEvent) => {
    if (clickAction === 'popup') {
      e.preventDefault();
    }
  };

  const linkAriaLabel = getLinkAriaLabel(link);

  const iconName =
    fileType === 'img' || !fileType ? 'default' : (icon ?? fileType);

  return (
    <Component
      aria-label={name}
      tabIndex={0}
      _fileBackgroundColor={fileBackgroundColor}
      _layout={layout}
      _fileBorderColor={fileBorderColor}
      _fileBorderSize={fileBorderSize}
      _nameColor={nameColor}
    >
      <BlockLink
        aria-label={linkAriaLabel}
        href={url}
        target="_blank"
        rel="noopener noreferrer nofollow"
        _layout={layout}
      >
        {((showIcon && fileType !== 'img') || previewImage) && (
          <StyledIcon
            iconClassName={getClassName('icon')}
            name={iconName}
            text={extension}
            size={{
              w: previewImageSize,
              h: previewImageSize
            }}
            previewImage={previewImage}
            altText={altText ?? ''}
            tabIndex={altText ? 0 : undefined}
          />
        )}

        {showIcon && fileType === 'img' && !previewImage && (
          <ImageWrapper _color={iconColor}>
            <ImageContainer>
              <Image src={url} />
            </ImageContainer>
          </ImageWrapper>
        )}

        <Wrapper _fontSize={nameTextStyle.fontSize} _layout={layout}>
          <Name textStyle={nameTextStyle} color={nameColor}>
            {name}

            {!url && <Error>No specified file</Error>}
          </Name>
        </Wrapper>
      </BlockLink>

      {showDownloadLink && downloadLinkText && (
        <LinkWrapper
          aria-label={linkAriaLabel}
          href={url}
          target="_blank"
          rel="noopener noreferrer nofollow"
          onClick={(e) => {
            clickHandler(e);
          }}
          download
          _layout={layout}
        >
          <Link textStyle={downloadTextStyle} color={downloadColor}>
            {downloadLinkText}
          </Link>
        </LinkWrapper>
      )}
    </Component>
  );
};

const Component = styled.div.attrs<{
  _fileBackgroundColor: string;
  _layout: string;
  _nameColor?: string;
  _fileBorderSize?: number;
  _fileBorderColor?: string;
}>({
  className: getClassName('component')
})`
  display: inline-flex;
  width: 100%;
  box-sizing: border-box;
  height: 100%;
  text-decoration: none;
  position: relative;
  overflow: hidden;
  flex-direction: ${({ _layout }) => (_layout === 'grid' ? 'column' : 'row')};
  align-items: ${({ _layout }) =>
    _layout === 'grid' ? 'flex-start' : 'center'};
  ${({ _fileBackgroundColor }) =>
    _fileBackgroundColor ? `background-color: ${_fileBackgroundColor};` : ''}
  ${({ _fileBackgroundColor }) =>
    _fileBackgroundColor && _fileBackgroundColor !== 'transparent'
      ? 'padding: 16px;'
      : 'margin-bottom: 16px;'}
  ${({ _fileBorderSize, _fileBorderColor }) =>
    _fileBorderSize && _fileBorderColor
      ? `border: ${_fileBorderSize}px solid ${_fileBorderColor};`
      : ''}

  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    display: block;
    opacity: 0;
    transition: 0.3s ease all;
    pointer-events: none;

    ${({ _fileBackgroundColor, _nameColor }) =>
      _nameColor &&
      _fileBackgroundColor &&
      _fileBackgroundColor !== 'transparent'
        ? `background-color: ${_nameColor};`
        : ''}
  }

  &:hover:before {
    opacity: 0.01;
  }

  &:not(:last-child) {
    margin-bottom: 20px;
  }
`;

const BlockLink = styled.a.attrs<{
  _layout: string;
}>({
  className: getClassName('blockLink')
})`
  text-decoration: none;
  line-height: 1.15;
  display: inline-flex;
  width: 100%;
  box-sizing: border-box;
  height: 100%;
  position: relative;
  overflow: hidden;
  flex-direction: ${({ _layout }) => (_layout === 'grid' ? 'column' : 'row')};

  &:hover {
    text-decoration: none;
  }
`;

const StyledIcon = styled(Icon).attrs({
  className: getClassName('iconContainer')
})`
  align-self: flex-start;
`;

const Wrapper = styled.div.attrs<{
  _fontSize: number;
  _layout: string;
}>({
  className: getClassName('wrapper')
})`
  display: flex;
  flex-direction: column;
  justify-content: center;
  font-size: ${({ _fontSize }) => _fontSize}px;

  &:not(:first-child) {
    margin-top: ${({ _layout }) => (_layout === 'grid' ? 12 : 0)}px;
    margin-left: ${({ _layout }) => (_layout === 'grid' ? 0 : 12)}px;
  }
`;

const Name = styled(Typography).attrs({
  className: getClassName('name')
})``;

const Error = styled.div.attrs({
  className: getClassName('error')
})`
  font-size: 11px;
  line-height: 14px;
  color: rgb(222, 43, 51);
`;

const LinkWrapper = styled.a.attrs<{
  _layout: string;
}>({
  className: getClassName('linkWrapper')
})`
  display: block;
  text-decoration: none;
  ${({ _layout }) => _layout === 'grid' && 'padding-top: 6px;'}
`;

const Link = styled(Typography).attrs({
  className: getClassName('link')
})`
  text-decoration: none;
  line-height: 1.15;

  &:hover {
    text-decoration: underline;
  }
`;

const ImageWrapper = styled.div.attrs<{
  _color: string;
}>({
  className: getClassName('icon')
})`
  display: block;
  ${({ _color }) =>
    _color && _color !== 'transparent' ? `color: ${_color};` : ''}
`;

const ImageContainer = styled.div.attrs({
  className: getClassName('imageContainer')
})`
  width: 30px;
  height: 38px;
  overflow: hidden;
  border-radius: 4px;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const Image = styled.img.attrs({
  className: getClassName('image')
})`
  height: 100%;
`;
